import React, { useEffect, useContext, useCallback, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import { PhoneContext } from '../../App';
import { setPhoneVisible } from '../../store/reducers/phoneSlice';
import AircallIcon from '../../assets/images/aircall-icon.svg';
import useInitializePhone from '../../pages/login/InitializePhone';
import { toast } from 'react-toastify';
import { Facility } from '../../models/FacilitySearchList';
import { clearIds } from '../../store/reducers/aircallTagSlice';
import { useNavigate } from 'react-router-dom';
import { useAddTagToCallMutation } from '../../api/endpoints/apiConfig';
import { Button, Link } from '@mui/material';
import { setIsDisposition, setisModal, setisOutbound } from '../../store/reducers/dispositionSlice';
import ConfirmationModal from '../../../src/declaration/Aircall/ConfirmationModal'
import LogRocket from "logrocket";
import {
  clearAllCallIdsSession,
  clearInboundCallsId,
  getCallIdForTagging,
  notifyCallsIdSessionChanged,
  setInboundCallsId,
} from '../../utils/callsIdSessionSync';
const AppLayout: React.FC = () => {
  const { initializePhone } = useInitializePhone();
  const phoneVisible = useAppSelector((state) => state.phone.phoneVisible);
  const phone = useContext(PhoneContext);
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [addTagToCall] = useAddTagToCallMutation();
  const isDisposition = useAppSelector((state) => state.disposition.isDisposition);
  const isModal = useAppSelector((state) => state.disposition.isModal);
  const isOutbound = useAppSelector((state) => state.disposition.isOutbound);
  // Effect to keep the dialer persistent across page navigations
  useEffect(() => {
    const phoneElement = document.querySelector("#phone");
    if (phoneVisible && phone && phoneElement) {
      if (!phoneElement.querySelector('iframe')) {
        phoneElement.innerHTML = `
                    <iframe 
                        allow="microphone; autoplay; clipboard-read; clipboard-write; hid" 
                        src="https://phone.aircall.io?integration=generic" 
                        style="height:600px; width:376px; border:none;">
                    </iframe>`;
      }
    } else if (phoneElement) {
      phoneElement.innerHTML = '';
    }
  }, [phoneVisible, phone]);
  const handleLogin = () => {
    dispatch(setPhoneVisible(true));
    initializePhone();
  };
  // Utility function to normalize the phone number
  const normalizePhoneNumber = (phoneNumber: string | null | undefined): string => {
    if (!phoneNumber) return '';
    // Replace non-numeric characters and remove any leading +1 for US numbers
    return phoneNumber.replace(/^\+?1/, '').replace(/\D/g, '');
  };
  // Implement the Dialer After Refresh
  const checkAircallPhoneNumber = useCallback((aircallPhone: string) => {
    // Retrieve setup data from session storage
    const setupData = JSON.parse(sessionStorage.getItem('setupData') || '{}');
    // Check if setupData exists and contains the setup array
    if (!setupData || !Array.isArray(setupData.setup)) {
      toast.error('No setup data found in session storage.');
      return;
    }

    // Retrieve operators data from session storage
    const operatorsData = JSON.parse(sessionStorage.getItem('operators') || '{}').operators;
    // Check if operatorsData exists and contains the operators array
    if (!operatorsData || !Array.isArray(operatorsData)) {
      toast.error('No operators data found in session storage.');
      return;
    }
    // Normalize incoming Aircall phone number
    const normalizedAircallPhone = normalizePhoneNumber(aircallPhone);
    // List of specific Aircall numbers that should trigger dashboard navigation
    const dashboardPhoneNumbers = setupData.setup.map((facility: any) =>
      normalizePhoneNumber(facility.aircall_phone)
    );
    const facilityRedirectNumbers = setupData.setup.map((facility: any) =>
      normalizePhoneNumber(facility.aircall_phone)
    );
    // Find the matching facility based on the normalized phone number
    const matchingFacility = setupData.setup.find((facility: Facility) => {
      const normalizedFacilityPhone = normalizePhoneNumber(facility.aircall_phone);
      return normalizedAircallPhone === normalizedFacilityPhone;
    });
    // Ensure redirection only happens after the check
    if (matchingFacility && dashboardPhoneNumbers.includes(normalizedAircallPhone)) {
      toast.success(`Matched facility: ${matchingFacility.location_code} with phone: ${matchingFacility.aircall_phone}`);
      // Save operator ID in sessionStorage
      sessionStorage.setItem('operatorId', matchingFacility.operator_id);
      dispatch(clearIds());
      navigate(`/dashboard/${matchingFacility.id}`);
      // Safely navigate to dashboard in a new tab
      if (matchingFacility.id) {
        dispatch(clearIds());
        navigate(`/dashboard/${matchingFacility.id}`);
      } else {
        toast.error('Matching facility does not have an ID.');
      }
    }
    else {
      // If no matching facility is found, check operatorsData
      const matchingOperator = operatorsData.find((operator: any) => {
        const normalizedOperatorPhone = normalizePhoneNumber(operator.aircall_phone);
        return normalizedAircallPhone === normalizedOperatorPhone;
      });
      // Check if a matching operator is found
      if (matchingOperator) {
        sessionStorage.setItem('operatorId', matchingOperator.id);
        toast.success(`Matched operator: ${matchingOperator.name} with phone: ${matchingOperator.aircall_phone}`);
        navigate('/Facility');
      } else {
        // If no operator is found, ensure no invalid access to undefined properties
        toast.error('No matching facility or operator found for this Aircall number. ' + normalizedAircallPhone);
        navigate('/Facility');
      }
    }
  }, [dispatch, navigate]);

  useEffect(() => {
    if (location.pathname === '/WaitingPage') {
      clearAllCallIdsSession();
    }
  }, [location.pathname]);

  // Integrating the Tag for the Refresh Aircall Dialer
  useEffect(() => {
    // let callAnswered = false;
    if (phone) {
      let storedPhoneNumber: string | null = null;
      // let storedPhoneNumber: string | null = null; // Global variable to store the phone number
      // Attach event listeners for incoming call
      phone.on('incoming_call', (callInfos: any) => {
        // Extract the incoming phone number from the call info
        const incomingPhoneNumber = callInfos.to;  // Assuming 'to' is the Aircall phone number being dialed
        const incomingPhones = callInfos.from;
        sessionStorage.setItem('incomingPhones', incomingPhones);
        // // Extract the call ID from the call info
        // const callsId = callInfos.call_id;  // Assuming 'call_id' is part of callInfos
        // // Store the call ID in session storage
        // sessionStorage.setItem('callsId', callsId);
        // Normalize the incoming phone number
        const normalizedIncomingPhoneNumber = normalizePhoneNumber(incomingPhoneNumber);
        // Store the phone number for later use
        storedPhoneNumber = normalizedIncomingPhoneNumber;
      });

      phone.on('outgoing_call', () => {
        toast.success('Outbound call started.', { toastId: 'aircall-outbound-started' });
      });

      phone.on('outgoing_answered', (callInfos: any) => {
        const callId = callInfos?.call_id;
        if (callId != null && callId !== '') {
          sessionStorage.setItem('callsId', String(callId));
          notifyCallsIdSessionChanged();
        }
      });

      // Attach event listeners for call end ringtone (triggered after the call ends)
      phone.on('call_end_ringtone', (callInfos: any) => {

        LogRocket.startNewSession();
        LogRocket.identify("sales-center", {
          name:  sessionStorage.getItem("FirstName") + ' ' +  sessionStorage.getItem("LastName"),
          subscriptionType: 'agent'
        });

        const callsId = callInfos.call_id;  // Assuming 'call_id' is part of callInfos
        // Store the call ID in session storage
        sessionStorage.setItem('callsId', callsId);
        setInboundCallsId(callsId);
        notifyCallsIdSessionChanged();
        sessionStorage.setItem('call_end_ringtone', callInfos.answer_status);
        const callersNumber = sessionStorage.getItem("incomingPhones") || '';
        sessionStorage.setItem('incomingPhonesFirst', callersNumber);

        // Use the stored phone number from the incoming call event
        if (storedPhoneNumber) {
          if (callInfos.answer_status === "answered") {
            checkAircallPhoneNumber(storedPhoneNumber);
          }
          else {
            // This else statement is reached every time an Agent does not answer the phone.
            // And displaying the message is annoying.  They see status of "cancelled.
            // No need to display anything here.
            //toast.error("unexpected answer status " + callInfos.answer_status);
          }
        } else {
          toast.error("No phone number stored from the incoming_call event.");
          console.error('No phone number stored from the incoming_call event.');
          //navigate('/WaitingPage');
        }
      });
      // Attach event listeners for call end (triggered after the call ends)
      phone.on('call_ended', (callInfos: any) => {
        sessionStorage.setItem('CallEndedValue', 'true');
        // Clear session storage and stored phone number
        storedPhoneNumber = null;
        // Helper function to process the "Qualified" or "Not Qualified" tag logic


        dispatch(setisModal(true));

      });
      //  const redialnum = sessionStorage.getItem('incomingPhones'); // Retrieve phone number from sessionStorage

      const handleDirectDial = () => {
        if (!phone || typeof phone.send !== "function") {
          console.error("Phone object is not initialized or invalid.");
          return;
        }

        const redialnum = sessionStorage.getItem("incomingPhonesFirst");
        console.log("Attempting to redial:", redialnum);

        if (!redialnum) {
          toast.error("No phone number available to redial.");
          return;
        }

        // Check the current phone status
        phone.send("get_status", {}, (statusSuccess: boolean, statusData: any) => {
          if (statusSuccess && statusData?.status === "in_call") {
            console.log("Already in a call. Ending the current call...");

            // Forcefully end the ongoing call
            phone.send("end_call", {}, (endSuccess: boolean, endData: any) => {
              if (endSuccess) {
                console.log("Current call ended successfully. Proceeding to dial...");
                initiateDialing(redialnum);
              } else {
                console.error("Failed to end the current call:", endData);
                toast.error("Unable to end the current call.");
              }
            });
          } else if (statusData?.status === "idle" || !statusData?.status) {
            // If no active call, proceed to dial
            initiateDialing(redialnum);
          } else {
            console.error("Unexpected phone status:", statusData?.status);
            toast.error("Unable to proceed. Unexpected phone status.");
          }
        });
      };

      // Helper function to initiate dialing
      const initiateDialing = (phoneNumber: string) => {
        phone.send("dial_number", { phone_number: phoneNumber }, (dialSuccess: boolean, dialData?: any) => {
          if (dialSuccess) {
            console.log("Outgoing call initiated successfully:", dialData);
            dispatch(setisModal(false));
            dispatch(setisOutbound(false));
          } else {
            console.error("Failed to dial the number:", dialData?.error_message || dialData);
            if (dialData?.code === "in_call") {
              console.warn("Phone is still in a call. Retrying...");
              handleDirectDial(); // Retry dialing after handling "in_call" state
            } else {
              toast.warn("Dialing failed. Please try again.");
            }
          }
        });
      };
      if (isOutbound) {
        handleDirectDial();
      }
    } else {
      // checkAircallPhoneNumber(staticCallInfo.to); // Use default staticCallInfo if needed
    }
  }, [phone, checkAircallPhoneNumber, addTagToCall, dispatch, isOutbound]);

  const handleConfirm = (isQualified: boolean) => {
    const qualifiedTagName = isQualified ? 'Qualified' : 'Not Qualified';
    // Step 1: Retrieve the callsId and tagsData from sessionStorage
    const callId = getCallIdForTagging();
    const tagsData = sessionStorage.getItem('tagsdata');
    const facilitydatas = sessionStorage.getItem('facilityData');
    const operatordata = sessionStorage.getItem('operators');

    // Step 2: Check if callId and tagsData exist
    if (callId && tagsData) {
      const parsedTagsData = JSON.parse(tagsData);
      const facilityData = facilitydatas ? JSON.parse(facilitydatas) : {};
      const operatordataObject = operatordata ? JSON.parse(operatordata) : {};

      // Find the operator name based on facility operator ID
      const operatorname = operatordataObject.operators.find((operator: { id: number }) => operator.id === facilityData[0]?.operator_id);
      if (!operatorname) {
        console.warn("Operator not found for the given facility operator ID");
        return;
      }
      const operatornames = operatorname.name === 'Store Space Self Storage' ? 'Store Space' : operatorname.name;
      // Find tag details for operator and facility
      const operatortagdetails = parsedTagsData.tags.find((tag: { name: string }) => tag.name === operatornames);
      const facilitytagdetails = parsedTagsData.tags.find((tag: { name: string }) => tag.name === facilityData[0]?.location_code);
      // Find the "Qualified" or "Not Qualified" tag and dispatch to Redux store
      const qualifiedTag = parsedTagsData.tags.find((tag: { id: number, name: string }) => tag.name === qualifiedTagName);
      const idsFromStorage = sessionStorage.getItem('ids');
      const storedIds = idsFromStorage ? JSON.parse(idsFromStorage) : [];
      const updatedIds = [...storedIds, operatortagdetails.id, facilitytagdetails.id, qualifiedTag.id];
      const tagData = { tags: updatedIds };
      addTagToCall({ callId, tagData })
        .unwrap()
        .then(() => {
          toast.dismiss();
          toast.success("Tag added to the call successfully.");
          dispatch(clearIds());
          clearInboundCallsId();
          sessionStorage.removeItem('callsId');
          notifyCallsIdSessionChanged();
          sessionStorage.removeItem('ids');
          //sessionStorage.removeItem('incomingPhones');
        })
        .catch((error) => {
          toast.dismiss();
          if (error.status) {
            // Handle specific HTTP status errors
            switch (error.status) {
              case 400:
                toast.error("Bad request. Please check the tag data.");
                break;
              case 404:
                toast.error("Call not found. Please verify the call ID.");
                break;
              case 500:
                toast.error("Server error. Please try again later.");
                break;
              default:
                toast.error(`Unexpected error occurred: ${error.statusText}`);
            }
          } else if (error.message) {
            // Handle network or JavaScript errors
            toast.error(`An error occurred: ${error.message}`);
          } else {
            // Handle unexpected errors
            toast.error("An unexpected error occurred. Please try again.");
          }
          console.error("Error adding tag to the call:", error);
        });

    } else {
      toast.error("callsId or tagsdata not found.");
    }
  };
  return (
    <>
      <div id="phone-container" className={phoneVisible ? 'phone-container' : 'd-none'}>
        <div className='arrow-up'></div>
        <div id="phone"></div>
      </div>
      <button
        id="phone-aircall"
        style={{
          position: 'fixed',
          top: '22px',
          right: '62px',
          cursor: 'pointer',
          zIndex: 9999, // Ensure it appears above other elements
          background: 'none', // Remove default button background
          border: 'none',     // Remove default button border
          padding: 0          // Remove default button padding
        }}
        onClick={handleLogin}
        aria-label="Toggle phone visibility"
      >
        <img
          style={{ height: '26px', width: '26px' }}
          src={AircallIcon}
          alt="Aircall Icon"
        />
      </button>
      <ConfirmationModal
        onConfirm={handleConfirm}
      />

      {/* Outlet renders the child routes */}
      <Outlet />
    </>
  );
};

export default AppLayout;
